import {auth, useAuth} from '@common/auth/use-auth';
import {canOpenStaffPage} from '@common/auth/staff-page-access';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {Link, Outlet, redirect, useLocation} from 'react-router';

/**
 * Renders the matched staff page only when config/staff-access.php allows it.
 * Wraps every /admin and /dashboard child route, so typing a URL does not show
 * a page the sidebar hides (the API refuses its data either way).
 */
export function StaffPageGate() {
  const {pathname} = useLocation();
  const {hasPermission, isLoggedIn} = useAuth();

  if (!isLoggedIn || canOpenStaffPage(pathname, hasPermission)) {
    return <Outlet />;
  }
  return <NoAccessMessage />;
}

/** Loader form, for route roots: redirect when the page is not allowed. */
export function staffPageGuard(url: string) {
  if (!auth.isLoggedIn) return redirect('/login');
  const {pathname} = new URL(url, window.location.origin);
  return canOpenStaffPage(pathname, name => auth.hasPermission(name)) ? null : redirect('/404');
}

function NoAccessMessage() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center px-16 py-40">
      <div className="max-w-440 text-center">
        <span className="material-icons mb-12 text-5xl text-muted">lock</span>
        <h1 className="mb-8 text-xl font-semibold">
          <Trans message="You don't have access to this page" />
        </h1>
        <p className="mb-20 text-sm text-muted">
          <Trans message="Your role does not include this area. Ask an administrator if you need it." />
        </p>
        <Button variant="outline" elementType={Link} to="/dashboard">
          <Trans message="Go to my workspace" />
        </Button>
      </div>
    </div>
  );
}
